import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Row, Col, Card, Badge, ToggleButton, ButtonGroup } from "react-bootstrap";
import { FaExchangeAlt, FaSearch, FaCamera, FaUsers, FaArrowRight, FaCheck } from "react-icons/fa";
import { getClassesBySchoolId, getStudentsByClassId, getAssessmentsByStudentId } from "../services/APIClient";

function SwapStudentsAssessmentModal({ show, onHide, schoolid, swapAssessmentStudents, transferAssessmentToStudent }) {
  // Add new state for one-way transfer mode
  const [isOneWayTransfer, setIsOneWayTransfer] = useState(false);
  
  const [allClasses, setAllClasses] = useState([]); // Store all classes (transformed)
  const [studentsA, setStudentsA] = useState([]); // Store students for Student A
  const [studentsB, setStudentsB] = useState([]); // Store students for Student B
  const [assessmentsA, setAssessmentsA] = useState([]); // Store assessments for Student A
  const [assessmentsB, setAssessmentsB] = useState([]); // Store assessments for Student B

  const [selectedClassA, setSelectedClassA] = useState(null); // Selected class for Student A
  const [selectedStudentA, setSelectedStudentA] = useState(null); // Selected student for Student A
  const [selectedAssessmentA, setSelectedAssessmentA] = useState(null); // Selected assessment for Student A

  const [selectedClassB, setSelectedClassB] = useState(null); // Selected class for Student B
  const [selectedStudentB, setSelectedStudentB] = useState(null); // Selected student for Student B
  const [selectedAssessmentB, setSelectedAssessmentB] = useState(null); // Selected assessment for Student B

  // Filter states
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [classSearchTerm, setClassSearchTerm] = useState("");

  const [statusMessage, setStatusMessage] = useState(""); // Status message for success/failure
  const [alertVariant, setAlertVariant] = useState(""); // Alert variant for Bootstrap
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // Fetch classes when the modal opens
  useEffect(() => {
    if (schoolid && show) {
      setLoading(true);
      getClassesBySchoolId(schoolid)
        .then((response) => {
          // Transform classes data to include enhanced properties
          const transformedClasses = (response || []).map(classItem => {
            const gradeLabel = getGradeLabelFromClass(classItem);
            const gradeBoolean = getGradeBooleanFromClass(classItem);
            
            return {
              id: classItem.id,
              name: classItem.name || 'Unnamed Class',
              grade: gradeLabel,
              gradeBoolean: gradeBoolean,
              academicYear: classItem.academicYear || null,
              snapshot: classItem.snapshot || false,
              studentNumber: classItem.studentNumber || 0,
              active: classItem.active,
              trial_class: classItem.trial_class,
              date_updated: classItem.date_updated
            };
          }).sort((a, b) => {
            // Sort by academic year (newest first), then by name
            if (a.academicYear !== b.academicYear) {
              return b.academicYear?.localeCompare(a.academicYear || '') || 0;
            }
            return a.name.localeCompare(b.name);
          });

          // Extract unique academic years for filter options
          const uniqueAcademicYears = [...new Set(transformedClasses
            .map(cls => cls.academicYear)
            .filter(year => year) // Remove null/undefined
          )].sort().reverse(); // Sort newest first

          const academicYearOptions = [
            { value: "all", label: "All Academic Years" },
            ...uniqueAcademicYears.map(year => ({
              value: year,
              label: `${year} Academic Year`
            }))
          ];

          setAvailableAcademicYears(academicYearOptions);
          setAllClasses(transformedClasses);
        })
        .catch((error) => {
          console.error("Error fetching classes:", error);
          setStatusMessage("Error fetching classes. Please try again.");
          setAlertVariant("danger");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [schoolid, show]);

  // Get filtered classes for dropdowns
  const getFilteredClasses = () => {
    return allClasses.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase());
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  // Fetch students when a class is selected for Student A
  useEffect(() => {
    if (selectedClassA) {
      getStudentsByClassId(schoolid, selectedClassA)
        .then((response) => {
          // Sort students alphabetically by name
          const sortedStudents = [...response].sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          setStudentsA(sortedStudents); // Store sorted students for Student A
        })
        .catch((error) => {
          console.error("Error fetching students for Student A:", error);
        });
    } else {
      setStudentsA([]);
      setSelectedStudentA(null);
      setAssessmentsA([]);
      setSelectedAssessmentA(null);
    }
  }, [selectedClassA]);

  // Fetch students when a class is selected for Student B
  useEffect(() => {
    if (selectedClassB) {
      getStudentsByClassId(schoolid, selectedClassB)
        .then((response) => {
          // Sort students alphabetically by name
          const sortedStudents = [...response].sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          setStudentsB(sortedStudents); // Store sorted students for Student B
        })
        .catch((error) => {
          console.error("Error fetching students for Student B:", error);
        });
    } else {
      setStudentsB([]);
      setSelectedStudentB(null);
      setAssessmentsB([]);
      setSelectedAssessmentB(null);
    }
  }, [selectedClassB]);

  // Fetch assessments for Student A when a student is selected
  useEffect(() => {
    if (selectedStudentA) {
      getAssessmentsByStudentId(schoolid, selectedStudentA)
        .then((response) => {
          setAssessmentsA(response); // Store assessments for Student A
        })
        .catch((error) => {
          console.error("Error fetching assessments for Student A:", error);
        });
    } else {
      setAssessmentsA([]);
      setSelectedAssessmentA(null);
    }
  }, [selectedStudentA]);

  // Fetch assessments for Student B when a student is selected
  useEffect(() => {
    if (selectedStudentB) {
      getAssessmentsByStudentId(schoolid, selectedStudentB)
        .then((response) => {
          setAssessmentsB(response); // Store assessments for Student B
        })
        .catch((error) => {
          console.error("Error fetching assessments for Student B:", error);
        });
    } else {
      setAssessmentsB([]);
      setSelectedAssessmentB(null);
    }
  }, [selectedStudentB]);

  // Updated to handle both swap and transfer
  const handleActionClick = () => {
    if (!selectedAssessmentA) {
      setStatusMessage("Please select an assessment for Student A.");
      setAlertVariant("warning");
      return;
    }
    
    if (!isOneWayTransfer && !selectedAssessmentB) {
      setStatusMessage("Please select an assessment for Student B for a two-way swap.");
      setAlertVariant("warning");
      return;
    }
    
    if (isOneWayTransfer && !selectedStudentB) {
      setStatusMessage("Please select a target student for the one-way transfer.");
      setAlertVariant("warning");
      return;
    }
    
    setShowConfirmModal(true);
  };

  // Updated to handle both swap and transfer actions
  const handleConfirmAction = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    
    try {
      if (isOneWayTransfer) {
        // Call the transfer function
        await transferAssessmentToStudent(schoolid, selectedAssessmentA, selectedStudentB);
        setStatusMessage("Assessment transfer was successful!");
      } else {
        // Call the swap function
        await swapAssessmentStudents(schoolid, selectedAssessmentA, selectedAssessmentB);
        setStatusMessage("Assessment swap was successful!");
      }
      setAlertVariant("success");
      clearFields();
    } catch (error) {
      setStatusMessage(isOneWayTransfer 
        ? "Assessment transfer was unsuccessful. Please try again." 
        : "Assessment swap was unsuccessful. Please try again.");
      setAlertVariant("danger");
    } finally {
      setLoading(false);
    }
  };

  // Clear form fields
  const clearFields = () => {
    setSelectedClassA(null);
    setSelectedStudentA(null);
    setSelectedAssessmentA(null);
    setStudentsA([]);
    setAssessmentsA([]);

    setSelectedClassB(null);
    setSelectedStudentB(null);
    setSelectedAssessmentB(null);
    setStudentsB([]);
    setAssessmentsB([]);
  };

  const handleClose = () => {
    clearFields();
    setStatusMessage("");
    setAlertVariant("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setClassSearchTerm("");
    setShowConfirmModal(false);
    setIsOneWayTransfer(false); // Reset transfer mode
    onHide();
  };

  const getSelectedClassInfo = (classId) => {
    return allClasses.find(cls => cls.id === classId);
  };

  const getSelectedStudentInfo = (students, studentId) => {
    return students.find(student => student.id === studentId);
  };

  const getSelectedAssessmentInfo = (assessments, assessmentId) => {
    return assessments.find(assessment => assessment.id === assessmentId);
  };

  const filteredClasses = getFilteredClasses();
  const classInfoA = getSelectedClassInfo(selectedClassA);
  const classInfoB = getSelectedClassInfo(selectedClassB);
  const studentInfoA = getSelectedStudentInfo(studentsA, selectedStudentA);
  const studentInfoB = getSelectedStudentInfo(studentsB, selectedStudentB);
  const assessmentInfoA = getSelectedAssessmentInfo(assessmentsA, selectedAssessmentA);
  const assessmentInfoB = getSelectedAssessmentInfo(assessmentsB, selectedAssessmentB);

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isOneWayTransfer ? (
              <FaArrowRight className="me-2" />
            ) : (
              <FaExchangeAlt className="me-2" />
            )}
            {isOneWayTransfer ? "Transfer Student Assessment" : "Swap Student Assessments"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '600px' }}>
          {statusMessage && (
            <Alert variant={alertVariant} className="mb-3" dismissible onClose={() => setStatusMessage("")}>
              {statusMessage}
            </Alert>
          )}

          {/* Mode Toggle */}
          <div className="mb-4">
            <h6 className="mb-2">Operation Mode</h6>
            <ButtonGroup className="w-100">
              <ToggleButton
                id="toggle-swap"
                type="radio"
                variant={isOneWayTransfer ? "outline-primary" : "primary"}
                name="operation-mode"
                value="swap"
                checked={!isOneWayTransfer}
                onChange={() => setIsOneWayTransfer(false)}
                className="d-flex align-items-center justify-content-center"
              >
                <FaExchangeAlt className="me-2" /> Two-way Swap
              </ToggleButton>
              <ToggleButton
                id="toggle-transfer"
                type="radio"
                variant={isOneWayTransfer ? "primary" : "outline-primary"}
                name="operation-mode"
                value="transfer"
                checked={isOneWayTransfer}
                onChange={() => setIsOneWayTransfer(true)}
                className="d-flex align-items-center justify-content-center"
              >
                <FaArrowRight className="me-2" /> One-way Transfer
              </ToggleButton>
            </ButtonGroup>
          </div>

          {/* Class Filters */}
          <div className="mb-4">
            <h6 className="mb-3">Filter Classes</h6>
            <Row>
              <Col md={3}>
                <Form.Select 
                  value={classAcademicYearFilter} 
                  onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {availableAcademicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={classGradeFilter} 
                  onChange={(e) => setClassGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={classSnapshotFilter} 
                  onChange={(e) => setClassSnapshotFilter(e.target.value)}
                  size="sm"
                >
                  {snapshotOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search classes..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>
          </div>

          <Row className="h-100">
            {/* Student A Section */}
            <Col md={5}>
              <Card className="h-100">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">
                    {isOneWayTransfer ? "Source Student" : "Student A"}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Class:</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedClassA || ""}
                      onChange={(e) => setSelectedClassA(e.target.value)}
                    >
                      <option value="">Select Class</option>
                      {filteredClasses.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name} - {cls.grade} {cls.academicYear && `(${cls.academicYear})`} 
                          {cls.snapshot && ' [Snapshot]'}
                          {!cls.snapshot && cls.studentNumber > 0 && ` - ${cls.studentNumber} students`}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  {classInfoA && (
                    <div className="mb-3 p-2 bg-light rounded">
                      <div className="d-flex align-items-center mb-1">
                        <strong className="me-2">{classInfoA.name}</strong>
                        {classInfoA.snapshot && (
                          <Badge bg="secondary">
                            <FaCamera className="me-1" size={10} />
                            Snapshot
                          </Badge>
                        )}
                        {!classInfoA.snapshot && classInfoA.studentNumber > 0 && (
                          <Badge bg="light" text="dark" className="ms-2">
                            <FaUsers className="me-1" size={10} />
                            {classInfoA.studentNumber} students
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        {classInfoA.grade}
                        {classInfoA.academicYear && ` • ${classInfoA.academicYear}`}
                      </small>
                    </div>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Student:</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedStudentA || ""}
                      onChange={(e) => setSelectedStudentA(e.target.value)}
                      disabled={!selectedClassA}
                    >
                      <option value="">Select Student</option>
                      {studentsA.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Assessment:</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedAssessmentA || ""}
                      onChange={(e) => setSelectedAssessmentA(e.target.value)}
                      disabled={!selectedStudentA}
                    >
                      <option value="">Select Assessment</option>
                      {assessmentsA.map((assessment) => (
                        <option key={assessment.id} value={assessment.id}>
                          {assessment.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  {selectedAssessmentA && (
                    <div className="mt-3 p-3 bg-success-light rounded border-start border-success border-3">
                      <h6 className="text-success mb-2">
                        <FaCheck className="me-2" />
                        Ready for {isOneWayTransfer ? "Transfer" : "Swap"}
                      </h6>
                      <div><strong>Student:</strong> {studentInfoA?.name}</div>
                      <div><strong>Assessment:</strong> {assessmentInfoA?.name}</div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Swap/Transfer Arrow */}
            <Col md={2} className="d-flex align-items-center justify-content-center">
              <div className="text-center">
                <div className={`${isOneWayTransfer ? 'bg-success' : 'bg-primary'} rounded-circle d-inline-flex align-items-center justify-content-center mb-2`} 
                     style={{ width: '50px', height: '50px' }}>
                  {isOneWayTransfer ? (
                    <FaArrowRight className="text-white" size={20} />
                  ) : (
                    <FaExchangeAlt className="text-white" size={20} />
                  )}
                </div>
                <div className="small text-muted">
                  {isOneWayTransfer ? "Transfer" : "Swap"}
                </div>
              </div>
            </Col>

            {/* Student B Section */}
            <Col md={5}>
              <Card className="h-100">
                <Card.Header className={`${isOneWayTransfer ? 'bg-success' : 'bg-info'} text-white`}>
                  <h5 className="mb-0">
                    {isOneWayTransfer ? "Target Student" : "Student B"}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Class:</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedClassB || ""}
                      onChange={(e) => setSelectedClassB(e.target.value)}
                    >
                      <option value="">Select Class</option>
                      {filteredClasses.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name} - {cls.grade} {cls.academicYear && `(${cls.academicYear})`} 
                          {cls.snapshot && ' [Snapshot]'}
                          {!cls.snapshot && cls.studentNumber > 0 && ` - ${cls.studentNumber} students`}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  {classInfoB && (
                    <div className="mb-3 p-2 bg-light rounded">
                      <div className="d-flex align-items-center mb-1">
                        <strong className="me-2">{classInfoB.name}</strong>
                        {classInfoB.snapshot && (
                          <Badge bg="secondary">
                            <FaCamera className="me-1" size={10} />
                            Snapshot
                          </Badge>
                        )}
                        {!classInfoB.snapshot && classInfoB.studentNumber > 0 && (
                          <Badge bg="light" text="dark" className="ms-2">
                            <FaUsers className="me-1" size={10} />
                            {classInfoB.studentNumber} students
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        {classInfoB.grade}
                        {classInfoB.academicYear && ` • ${classInfoB.academicYear}`}
                      </small>
                    </div>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Student:</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedStudentB || ""}
                      onChange={(e) => setSelectedStudentB(e.target.value)}
                      disabled={!selectedClassB}
                    >
                      <option value="">Select Student</option>
                      {studentsB.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  {/* Only show assessment selector in swap mode */}
                  {!isOneWayTransfer && (
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Assessment:</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedAssessmentB || ""}
                        onChange={(e) => setSelectedAssessmentB(e.target.value)}
                        disabled={!selectedStudentB}
                      >
                        <option value="">Select Assessment</option>
                        {assessmentsB.map((assessment) => (
                          <option key={assessment.id} value={assessment.id}>
                            {assessment.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}

                  {/* Show success indicator for swap or transfer */}
                  {(isOneWayTransfer ? selectedStudentB : selectedAssessmentB) && (
                    <div className="mt-3 p-3 bg-success-light rounded border-start border-success border-3">
                      <h6 className="text-success mb-2">
                        <FaCheck className="me-2" />
                        Ready for {isOneWayTransfer ? "Transfer" : "Swap"}
                      </h6>
                      <div><strong>Student:</strong> {studentInfoB?.name}</div>
                      {!isOneWayTransfer && (
                        <div><strong>Assessment:</strong> {assessmentInfoB?.name}</div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="text-muted">
              {selectedAssessmentA && (isOneWayTransfer ? selectedStudentB : selectedAssessmentB) && (
                <span>
                  {isOneWayTransfer ? (
                    <FaArrowRight className="me-1" />
                  ) : (
                    <FaExchangeAlt className="me-1" />
                  )}
                  {isOneWayTransfer ? (
                    <>Ready to transfer assessment from {studentInfoA?.name} to {studentInfoB?.name}</>
                  ) : (
                    <>Ready to swap assessments between {studentInfoA?.name} and {studentInfoB?.name}</>
                  )}
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button 
                variant="success" 
                onClick={handleActionClick}
                disabled={
                  !selectedAssessmentA || 
                  (isOneWayTransfer ? !selectedStudentB : !selectedAssessmentB) || 
                  loading
                }
              >
                {loading ? "Processing..." : isOneWayTransfer ? "Transfer Assessment" : "Swap Assessments"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            {isOneWayTransfer ? (
              <FaArrowRight className="me-2" />
            ) : (
              <FaExchangeAlt className="me-2" />
            )}
            Confirm Assessment {isOneWayTransfer ? "Transfer" : "Swap"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-warning-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                 style={{ width: '60px', height: '60px' }}>
              {isOneWayTransfer ? (
                <FaArrowRight className="text-warning" size={24} />
              ) : (
                <FaExchangeAlt className="text-warning" size={24} />
              )}
            </div>
            <h5 className="mb-2">{isOneWayTransfer ? "Transfer" : "Swap"} Student Assessment{isOneWayTransfer ? "" : "s"}</h5>
            <p className="text-muted">Please review the {isOneWayTransfer ? "transfer" : "swap"} details carefully before confirming</p>
          </div>

          {/* Swap/Transfer Details */}
          <div className="rounded-3 p-4 mb-4" 
               style={{ background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)' }}>
            <div className="row align-items-center">
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">FROM</div>
                  <div className="fw-bold">{studentInfoA?.name}</div>
                  <div className="small text-muted">{classInfoA?.name}</div>
                  <Badge bg="primary" className="mt-1">{assessmentInfoA?.name}</Badge>
                </div>
              </div>
              <div className="col-2 text-center">
                <div className="bg-warning rounded-circle d-inline-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px' }}>
                  {isOneWayTransfer ? (
                    <FaArrowRight className="text-dark" size={16} />
                  ) : (
                    <FaExchangeAlt className="text-dark" size={16} />
                  )}
                </div>
              </div>
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">TO</div>
                  <div className="fw-bold">{studentInfoB?.name}</div>
                  <div className="small text-muted">{classInfoB?.name}</div>
                  {!isOneWayTransfer && (
                    <Badge bg="info" className="mt-1">{assessmentInfoB?.name}</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="alert alert-info d-flex align-items-start" role="alert">
            <div className="me-2">ℹ️</div>
            <div>
              {isOneWayTransfer ? (
                <><strong>Assessment Transfer:</strong> This will move the assessment from {studentInfoA?.name} to {studentInfoB?.name}.</>
              ) : (
                <><strong>Assessment Swap:</strong> This will exchange the assessments between the two selected students. 
                {studentInfoA?.name} will receive {assessmentInfoB?.name} and {studentInfoB?.name} will receive {assessmentInfoA?.name}.</>
              )}
            </div>
          </div>

          {(classInfoA?.snapshot || classInfoB?.snapshot) && (
            <div className="alert alert-warning d-flex align-items-start" role="alert">
              <div className="me-2">⚠️</div>
              <div>
                <strong>Snapshot Class Warning:</strong> One or both students are in snapshot classes. 
                {isOneWayTransfer ? "Transferring" : "Swapping"} assessments in historical records may affect data integrity.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} className="me-2">
            Cancel
          </Button>
          <Button variant="warning" onClick={handleConfirmAction}>
            Confirm {isOneWayTransfer ? "Transfer" : "Swap"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SwapStudentsAssessmentModal;